import { useCallback, useEffect, useState } from "react";
import { Loader2, MapPin } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { getPosition } from "@/lib/geo";

const GATED_ROLES = ["travel_trainer", "admin_manager"];
const SNOOZE_MINUTES = 10;          // acknowledged reminder returns after this if GPS is still off
const CHECK_INTERVAL_MS = 2 * 60 * 1000;
const SNOOZE_KEY = "gps-reminder-snooze-until"; // sessionStorage so it survives page refreshes too

const getSnooze = () => parseInt(sessionStorage.getItem(SNOOZE_KEY) || "0", 10);

/**
 * Non-blocking GPS/shift reminder for field staff. Staff CAN use the app without GPS:
 * if they are off shift or GPS is off, a popup asks them to turn it on; acknowledging
 * dismisses it; it returns after SNOOZE_MINUTES if the situation hasn't changed.
 * The office is still alerted separately (GPS-silent / missed sign-in alerts).
 */
export default function ShiftGate() {
  const { user } = useAuth();
  const [prompt, setPrompt] = useState(null); // null | "shift" | "gps"
  const [busy, setBusy] = useState(false);
  const gated = GATED_ROLES.includes(user?.role);

  const evaluate = useCallback(async () => {
    if (!gated || Date.now() < getSnooze()) return;
    try {
      const { data } = await api.get("/attendance/status");
      if (!data.shift) {
        setPrompt("shift");
        return;
      }
      try {
        await getPosition();
        setPrompt(null); // on shift and GPS working — nothing to remind about
      } catch {
        setPrompt("gps");
      }
    } catch {
      setPrompt(null); // never block on an API error
    }
  }, [gated]);

  useEffect(() => {
    if (!gated) return undefined;
    const first = setTimeout(evaluate, 1500);
    const interval = setInterval(evaluate, CHECK_INTERVAL_MS);
    const onLost = () => { if (Date.now() >= getSnooze()) setPrompt("gps"); };
    const onRestored = () => setPrompt(null);
    const onShiftChanged = () => evaluate();
    window.addEventListener("gps-lost", onLost);
    window.addEventListener("gps-restored", onRestored);
    window.addEventListener("shift-changed", onShiftChanged);
    return () => {
      clearTimeout(first);
      clearInterval(interval);
      window.removeEventListener("gps-lost", onLost);
      window.removeEventListener("gps-restored", onRestored);
      window.removeEventListener("shift-changed", onShiftChanged);
    };
  }, [gated, evaluate]);

  if (!gated || !prompt) return null;

  const dismiss = () => {
    sessionStorage.setItem(SNOOZE_KEY, String(Date.now() + SNOOZE_MINUTES * 60 * 1000));
    setPrompt(null);
  };

  const startShift = async () => {
    setBusy(true);
    try {
      const pos = await getPosition();
      await api.post("/attendance/sign-in", {
        lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy,
      });
      window.dispatchEvent(new Event("shift-changed"));
      setPrompt(null);
      toast.success("Shift started — the office has been notified");
    } catch (err) {
      if (err?.response) {
        toast.error(formatApiError(err));
      } else {
        setPrompt("gps"); // location failed — switch to the GPS prompt
      }
    } finally {
      setBusy(false);
    }
  };

  const retryGps = async () => {
    setBusy(true);
    try {
      const pos = await getPosition();
      await api.post("/attendance/location", {
        lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy,
      });
      window.dispatchEvent(new Event("gps-restored"));
      setPrompt(null);
      toast.success("GPS is on — thank you");
    } catch {
      /* stay on the GPS prompt */
    } finally {
      setBusy(false);
    }
  };

  const isShiftPrompt = prompt === "shift";

  return (
    <div data-testid="shift-gate" className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/70 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-amber-100">
          <MapPin className="h-7 w-7 text-amber-600" />
        </div>
        <h1 className="mt-4 text-center font-heading text-xl font-bold text-slate-900" data-testid="shift-gate-title">
          {isShiftPrompt ? "You're not signed in on shift" : "GPS is turned off"}
        </h1>
        <p className="mt-2 text-center text-sm leading-relaxed text-slate-600">
          {isShiftPrompt
            ? "Office policy: please start your shift with GPS turned on — your position is shared with the office while you are on shift."
            : "You are on shift but your location is not being shared with the office. Please turn on Location/GPS (quick settings, then allow it for this site when asked)."}
        </p>
        <p className="mt-2 text-center text-xs text-slate-500">
          You can continue without it for now — this reminder will come back in {SNOOZE_MINUTES} minutes if GPS is still off.
        </p>

        <button
          type="button"
          data-testid="shift-gate-start-button"
          onClick={isShiftPrompt ? startShift : retryGps}
          disabled={busy}
          className="mt-5 min-h-[48px] w-full rounded-lg bg-emerald-600 px-4 py-3 text-sm font-semibold text-white transition-colors duration-200 hover:bg-emerald-500 disabled:opacity-60"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin inline" /> : isShiftPrompt ? "Start shift with GPS" : "Retry — I've turned GPS on"}
        </button>
        <button
          type="button"
          data-testid="shift-gate-dismiss"
          onClick={dismiss}
          className="mt-3 min-h-[44px] w-full rounded-lg bg-slate-100 px-4 py-2.5 text-sm font-medium text-slate-700 transition-colors duration-200 hover:bg-slate-200"
        >
          I acknowledge — continue without GPS (remind me in {SNOOZE_MINUTES} min)
        </button>
      </div>
    </div>
  );
}
